/**
 * Faraid Calculator Types
 * Type definitions for Islamic inheritance calculation based on FaraidInfo.md
 * 
 * Reference: K-Faraid MAIS (Majlis Agama Islam Selangor)
 */

// ============================================================================
// Deceased (Simati) Types
// ============================================================================

/**
 * Gender of the deceased (Simati)
 */
export type DeceasedGender = 'lelaki' | 'perempuan';

/**
 * Information about the deceased
 */
export interface DeceasedInfo {
  gender: DeceasedGender;
}

// ============================================================================
// Heir (Waris) Types
// ============================================================================

/**
 * All possible heir types in Faraid system
 * Based on FaraidInfo.md Section 3 (Waris-Waris)
 */
export type HeirType =
  // Waris Utama (Primary Heirs)
  | 'suami'                    // Husband
  | 'isteri'                   // Wife(s) - can be up to 4
  | 'bapa'                     // Father
  | 'ibu'                      // Mother
  | 'anak_lelaki'              // Son(s)
  | 'anak_perempuan'           // Daughter(s)
  
  // Waris Ganti (Substitute Heirs)
  | 'datuk'                    // Paternal grandfather (Bapa kepada Bapa)
  | 'nenek_sebelah_bapa'       // Paternal grandmother (Ibu kepada Bapa)
  | 'nenek_sebelah_ibu'        // Maternal grandmother (Ibu kepada Ibu)
  | 'cucu_lelaki'              // Grandson through son (Anak Lelaki kepada Anak Lelaki)
  | 'cucu_perempuan'           // Granddaughter through son (Anak Perempuan kepada Anak Lelaki)
  | 'cicit_lelaki'             // Great-grandson through son's son (Anak Lelaki kepada Anak Lelaki kepada Anak Lelaki)
  | 'cicit_perempuan'          // Great-granddaughter through son's son (Anak Perempuan kepada Anak Lelaki kepada Anak Lelaki)
  
  // Waris Kedua (Secondary Heirs) - Siblings
  | 'saudara_seibu_sebapa_lelaki'      // Full brother (SIBL)
  | 'saudara_seibu_sebapa_perempuan'   // Full sister (SIBP)
  | 'saudara_sebapa_lelaki'            // Paternal half-brother (SBL)
  | 'saudara_sebapa_perempuan'         // Paternal half-sister (SBP)
  | 'saudara_seibu_lelaki'             // Maternal half-brother (SIL)
  | 'saudara_seibu_perempuan'          // Maternal half-sister (SIP)
  
  // Anak Saudara (Nephews)
  | 'anak_saudara_seibu_sebapa_lelaki' // Son of full brother (Anak Lelaki kepada SIBL)
  | 'anak_saudara_sebapa_lelaki'       // Son of paternal half-brother (Anak Lelaki kepada SBL)
  
  // Bapa Saudara (Uncles) - Paternal
  | 'bapa_saudara_seibu_sebapa'        // Full paternal uncle (SIBL kepada Bapa / Bapa Saudara)
  | 'bapa_saudara_sebapa'              // Paternal half-uncle (SBL kepada Bapa / Bapa Saudara)
  
  // Anak Bapa Saudara (Cousins) - Paternal
  | 'anak_bapa_saudara_seibu_sebapa'   // Son of full paternal uncle (Anak Lelaki kepada SIBL kepada Bapa / Sepupu)
  | 'anak_bapa_saudara_sebapa'         // Son of paternal half-uncle (Anak Lelaki kepada SBL kepada Bapa / Sepupu)
  
  // Datuk Saudara (Great-uncles) - From grandfather
  | 'datuk_saudara_seibu_sebapa'       // Full brother of paternal grandfather (SIBL Datuk)
  | 'datuk_saudara_sebapa'             // Paternal half-brother of grandfather (SBL Datuk)
  | 'anak_datuk_saudara_seibu_sebapa'  // Son of grandfather's full brother (Anak Lelaki SIBL Datuk)
  | 'anak_datuk_saudara_sebapa';       // Son of grandfather's paternal half-brother (Anak Lelaki SBL Datuk)

/**
 * Heir category for grouping in UI
 */
export type HeirCategory = 
  | 'waris_utama'     // Primary heirs (spouse, parents, children)
  | 'waris_ganti'     // Substitute heirs (grandparents, grandchildren)
  | 'waris_kedua';    // Secondary heirs (siblings, nephews, uncles, cousins)

/**
 * Share type for heirs
 * - fardu: Fixed share prescribed in Quran
 * - asabah: Residual share (takes remainder)
 * - mahjub: Blocked/excluded by other heirs
 */
export type ShareType = 'fardu' | 'asabah' | 'mahjub';

/**
 * Fixed share fractions (Bahagian Fardu)
 * Based on FaraidInfo.md Section 4.1.1
 */
export type FixedShareFraction = 
  | '1/2'   // Satu Perdua
  | '1/3'   // Satu Pertiga
  | '1/4'   // Satu Perempat
  | '2/3'   // Dua Pertiga
  | '1/6'   // Satu Perenam
  | '1/8'   // Satu Perlapan
  | '1/3_baki'; // Satu Pertiga Baki (for 'Umariyyatain cases)

/**
 * Heir configuration for input
 */
export interface HeirInput {
  type: HeirType;
  count: number;
  isPresent: boolean;
}

/**
 * Complete heir state with all inputs
 */
export interface HeirsState {
  deceased: DeceasedInfo;
  heirs: Record<HeirType, HeirInput>;
}

/**
 * Heir definition for display and calculation
 */
export interface HeirDefinition {
  type: HeirType;
  labelMs: string;        // Malay label
  labelEn: string;        // English label
  category: HeirCategory;
  gender: 'lelaki' | 'perempuan';
  maxCount: number;       // Maximum count (e.g., isteri can be up to 4)
  canHaveMultiple: boolean;
  /** Heirs that block this heir completely (Hijab Hirman) */
  blockedBy: HeirType[];
  /** Special conditions for eligibility */
  specialConditions?: string;
}

// ============================================================================
// Asset (Harta Pusaka) Types
// ============================================================================

/**
 * Asset type categories
 * Based on FaraidInfo.md Section 2.1 (Rukun Pusaka - Harta)
 */
export type AssetType = 
  | 'tanah'                   // Land
  | 'rumah_bangunan'          // House/Building
  | 'kenderaan'               // Vehicle
  | 'tabung_haji'             // Tabung Haji
  | 'kwsp'                    // EPF/KWSP
  | 'simpanan'                // Savings
  | 'pelaburan_unit_trust'    // Investments/Unit Trust
  | 'syer'                    // Shares
  | 'hasil_sewa'              // Rental income
  | 'barang_kemas'            // Jewelry
  | 'lain_lain';              // Others

/**
 * Individual asset entry
 */
export interface AssetEntry {
  id: string;
  type: AssetType;
  description?: string;
  value: number;
}

/**
 * Assets state
 */
export interface AssetsState {
  assets: AssetEntry[];
  totalValue: number;
}

// ============================================================================
// Calculation Result Types
// ============================================================================

/**
 * Share calculation result for a single heir group
 */
export interface ShareResult {
  heirType: HeirType;
  heirLabel: string;
  count: number;
  shareType: ShareType;
  /** Fixed share fraction if applicable */
  fixedShare?: FixedShareFraction;
  /** Fraction as numerator/denominator for calculation */
  fractionNumerator: number;
  fractionDenominator: number;
  /** Percentage of total estate */
  percentage: number;
  /** Total amount for this heir group */
  totalAmount: number;
  /** Amount per individual heir */
  amountPerHeir: number;
  /** Explanation in Malay */
  explanationMs?: string;
  /** Whether this heir is blocked (mahjub) */
  isBlocked: boolean;
  /** Who blocked this heir */
  blockedByHeirs?: HeirType[];
}

/**
 * Special case indicators
 */
export interface SpecialCaseInfo {
  /** 'Aul case - shares exceed 1, need adjustment */
  isAul: boolean;
  aulOriginalDenominator?: number;
  aulNewDenominator?: number;
  
  /** Radd case - shares less than 1, remainder returned to heirs */
  isRadd: boolean;
  
  /** 'Umariyyatain/Gharrawain case */
  isUmariyyatain: boolean;
  
  /** Musytarikah case */
  isMusytarikah: boolean;
  
  /** Datuk with siblings case */
  isDatukWithSiblings: boolean;
}

/**
 * Complete calculation result
 */
export interface FaraidCalculationResult {
  /** Total estate value */
  totalEstate: number;
  
  /** Base denominator (Asal Masalah) before 'Aul adjustment */
  baseDenominator: number;
  
  /** Final denominator after 'Aul adjustment if applicable */
  finalDenominator: number;
  
  /** Individual share results */
  shares: ShareResult[];
  
  /** Total distributed amount */
  totalDistributed: number;
  
  /** Remainder to Baitulmal if any */
  remainderToBaitulmal: number;
  
  /** Special case information */
  specialCases: SpecialCaseInfo;
  
  /** Validation errors if any */
  errors: string[];
  
  /** Warnings (non-blocking issues) */
  warnings: string[];
  
  /** Whether calculation is valid */
  isValid: boolean;
}

// ============================================================================
// Calculator State Types
// ============================================================================

/**
 * Active tab in the calculator
 */
export type FaraidTab = 'senarai_waris' | 'carta_waris' | 'harta_pusaka' | 'pengiraan';

/**
 * Complete calculator state
 */
export interface FaraidCalculatorState {
  activeTab: FaraidTab;
  deceased: DeceasedInfo;
  heirs: Record<HeirType, HeirInput>;
  assets: AssetsState;
  calculationResult: FaraidCalculationResult | null;
  isCalculating: boolean;
  lastCalculatedAt: string | null;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Asset type labels
 */
export const ASSET_TYPE_LABELS: Record<AssetType, { ms: string; en: string }> = {
  tanah: { ms: 'Tanah', en: 'Land' },
  rumah_bangunan: { ms: 'Rumah / Bangunan', en: 'House / Building' },
  kenderaan: { ms: 'Kenderaan', en: 'Vehicle' },
  tabung_haji: { ms: 'Tabung Haji', en: 'Tabung Haji' },
  kwsp: { ms: 'KWSP', en: 'EPF' },
  simpanan: { ms: 'Simpanan', en: 'Savings' },
  pelaburan_unit_trust: { ms: 'Pelaburan / Unit Trust', en: 'Investment / Unit Trust' },
  syer: { ms: 'Syer', en: 'Shares' },
  hasil_sewa: { ms: 'Hasil Sewa', en: 'Rental Income' },
  barang_kemas: { ms: 'Barang Kemas', en: 'Jewelry' },
  lain_lain: { ms: 'Lain-lain', en: 'Others' },
};

/**
 * Asset types array for iteration
 */
export const ASSET_TYPES: AssetType[] = [
  'tanah',
  'rumah_bangunan',
  'kenderaan',
  'tabung_haji',
  'kwsp',
  'simpanan',
  'pelaburan_unit_trust',
  'syer',
  'hasil_sewa',
  'barang_kemas',
  'lain_lain',
];

/**
 * Fraction display labels
 */
export const FRACTION_LABELS: Record<FixedShareFraction, string> = {
  '1/2': '1/2',
  '1/3': '1/3',
  '1/4': '1/4',
  '2/3': '2/3',
  '1/6': '1/6',
  '1/8': '1/8',
  '1/3_baki': '1/3 Baki',
};
